import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';

interface ActiveSessionProps {
  session: {
    workRecordId: string;
    providerId: string;
    clientId: string;
    clientName?: string;
    actualStartTime: string;
    actualEndTime?: string;
    jobType: string;
  };
  onEndSession: () => void;
  onConfirmCheckOut: () => void;
}

const ActiveSession: React.FC<ActiveSessionProps> = ({ 
  session, 
  onEndSession, 
  onConfirmCheckOut 
}) => {
  const { t } = useTranslation();
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    const updateElapsedTime = () => {
      const startTime = new Date(session.actualStartTime).getTime();
      const now = new Date().getTime();
      const elapsed = now - startTime;
      
      const hours = Math.floor(elapsed / (1000 * 60 * 60));
      const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
      
      setElapsedTime(`${hours}h ${minutes}m`);
    };

    // Update immediately
    updateElapsedTime();
    
    // Update every minute
    const interval = setInterval(updateElapsedTime, 60000);
    
    return () => clearInterval(interval);
  }, [session.actualStartTime]);

  const handleEndSession = () => {
    setIsConfirming(true);
  };

  const handleConfirmCheckOut = () => {
    onConfirmCheckOut();
    setIsConfirming(false);
  };

  const handleCancel = () => {
    setIsConfirming(false);
  };

  const formatStartTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isConfirming) {
    return (
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('checkOutConfirmation')}
        </h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-center mb-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                {t('confirmCheckOut')}
              </h3>
            </div>
          </div>
          <div className="text-sm text-yellow-700">
            <p className="mb-2">
              <strong>{t('client')}:</strong> {session.clientName || session.clientId}
            </p>
            <p className="mb-2">
              <strong>{t('startTime')}:</strong> {formatStartTime(session.actualStartTime)}
            </p>
            <p className="mb-2">
              <strong>{t('timeWorked')}:</strong> {elapsedTime}
            </p>
            <p>
              {t('sessionActive')}: {elapsedTime}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleConfirmCheckOut}
            className="btn-primary flex-1"
          >
            {t('confirmCheckOut')}
          </button>
          <button
            onClick={handleCancel}
            className="btn-secondary flex-1"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        {t('activeSession')}
      </h2>
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
        <div className="flex items-center mb-3">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              {t('sessionActive')}
            </h3>
          </div>
        </div>
        <div className="text-sm text-green-700">
          <p className="mb-2">
            <strong>{t('client')}:</strong> {session.clientName || session.clientId}
          </p>
          <p className="mb-2">
            <strong>{t('startTime')}:</strong> {formatStartTime(session.actualStartTime)}
          </p>
          <p className="mb-2">
            <strong>{t('sessionDuration')}:</strong> {elapsedTime}
          </p>
          <p className="text-xs italic">
            {t('sessionActive')}: {elapsedTime}
          </p>
        </div>
      </div>
      <button
        onClick={handleEndSession}
        className="btn-primary w-full"
      >
        {t('endSession')}
      </button>
    </div>
  );
};

export default ActiveSession; 